import React, { useState } from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Palette } from "lucide-react";

import {
  AddColorAction,
  UpdateColorAction,
} from "store/middlewares/dataManagement/color";
import { Input, Label, Button } from "components/ui";
import { SubmitLoaderButton } from "components/atoms";
import { closeAdminModal } from "store/slices/common/adminModal.slice";

function ModalEditColor({ row, filter }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState(false);

  const handleSubmit = (values) => {
    let tempObj = { ...values, name: values.name.trim() };
    if (row) {
      tempObj = { ...tempObj, id: row?.id };
      dispatch(
        UpdateColorAction({
          ...tempObj,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        }),
      );
    } else {
      dispatch(
        AddColorAction({
          ...tempObj,
          filter,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      name: row?.name ? row?.name : "",
      code: row?.code ? row?.code : "#d0021b",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Color name is required"),
      code: Yup.string()
        .required("Color code is required")
        .test(
          "not-white",
          "White color is not allowed",
          (value) => value?.toLowerCase() !== "#ffffff",
        ),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <Palette className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {row ? "Edit color" : "Add color"}
        </h2>
      </div>

      {/* Inputs (Stacked label + input) */}
      <div className="grid gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Name</Label>
          <Input
            error={formik.errors.name && formik.touched.name}
            errorMsg={formik.errors.name}
            value={formik.values.name}
            type="text"
            name="name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isTextOnly={true}
            isSpace={true}
            placeholder="Enter name"
            className="w-full"
            variant="lg"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Color Code</Label>
          <Input
            error={formik.errors.code && formik.touched.code}
            errorMsg={formik.errors.code}
            value={formik.values.code}
            type="color"
            name="code"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter color code"
            className="w-full"
            variant="lg"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText={row ? "Update" : "Add"}
          isDisable={formik.isSubmitting && !formError}
        />
      </div>
    </form>
  );
}

export default ModalEditColor;